'use client';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Protect, ProtectProps } from '../Protect';
import { ProtectFeatureFlag } from '../ProtectFeatureFlag';

export interface NavigationItem {
  label: string;
  href?: string;
  children?: NavigationItem[];
  icon?: React.ReactNode;
  protectWith?: ProtectProps['roles'];
  flags?: string[];
}

export interface NavigationMenuProps {
  items: NavigationItem[];
  onNavigate?: () => void;
}

export function NavigationMenu({ items, onNavigate }: NavigationMenuProps) {
  const pathname = usePathname();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Auto-expand items that contain the current path
  useEffect(() => {
    const newExpanded = new Set<string>();
    items.forEach(item => {
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some(
          child => child.href && pathname.startsWith(child.href),
        );
        if (hasActiveChild) {
          newExpanded.add(item.label);
        }
      }
    });
    setExpandedItems(newExpanded);
  }, [pathname, items]);

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent, label: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded(label);
    }
  };

  const isActive = (href?: string) => {
    if (!href) return false;
    return pathname === href || pathname.startsWith(href + '/');
  };

  const renderItem = (item: NavigationItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.label);
    const active = isActive(item.href);

    // Helper component to wrap item with protection
    const ProtectedItem = ({ children }: { children: React.ReactNode }) => {
      let content = children;

      // Apply role protection if needed
      if (item.protectWith && item.protectWith.length > 0) {
        content = <Protect roles={item.protectWith}>{content}</Protect>;
      }

      // Apply feature flag protection if needed
      if (item.flags && item.flags.length > 0) {
        content = <ProtectFeatureFlag flags={item.flags}>{content}</ProtectFeatureFlag>;
      }

      return <>{content}</>;
    };

    const itemContent = hasChildren ? (
      <li key={item.label} role="none">
        <button
          type="button"
          onClick={() => toggleExpanded(item.label)}
          onKeyDown={e => handleKeyDown(e, item.label)}
          className={clsx(
            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors',
            'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
            level === 0 ? 'text-gray-900' : 'text-gray-700',
            level > 0 && 'ml-4',
          )}
          aria-expanded={isExpanded}
          aria-controls={`nav-submenu-${item.label}`}
          aria-label={`${item.label} menu, ${isExpanded ? 'expanded' : 'collapsed'}`}
        >
          <span className="flex items-center gap-2">
            {item.icon}
            {item.label}
          </span>
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        <ul
          id={`nav-submenu-${item.label}`}
          role="group"
          className={clsx(
            'mt-1 space-y-1 overflow-hidden transition-all duration-200',
            isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
          )}
          aria-hidden={!isExpanded}
        >
          {item.children?.map(child => renderItem(child, level + 1))}
        </ul>
      </li>
    ) : (
      <li key={item.label} role="none">
        <Link
          href={item.href || '#'}
          onClick={onNavigate}
          className={clsx(
            'block rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
            active
              ? 'bg-indigo-50 text-indigo-700'
              : level === 0
                ? 'text-gray-900'
                : 'text-gray-700',
            level > 0 && 'ml-4',
          )}
          aria-current={active ? 'page' : undefined}
        >
          <span className="flex items-center gap-2">
            {item.icon}
            {item.label}
          </span>
        </Link>
      </li>
    );

    return <ProtectedItem key={item.label}>{itemContent}</ProtectedItem>;
  };

  return (
    <nav aria-label="Main navigation">
      <ul role="list" className="space-y-1">
        {items.map(item => renderItem(item))}
      </ul>
    </nav>
  );
}
